import { createContext, type ReactNode, type RefObject, useContext, useRef, useState } from "react";

/**
 * App-shell layout state, modelled 1:1 on planung-frontend's AppLayoutContext:
 * the DS `Sidebar` owns expand (hover) / pin, so we only hold `sidebarIsOpen`
 * (expanded) + `sidebarIsFixed` (pinned) + the content `scrollRef`. No
 * persistence — like planung, it resets on reload.
 */
type AppLayoutValue = {
  sidebarIsOpen: boolean;
  setSidebarIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  sidebarIsFixed: boolean;
  setSidebarIsFixed: (value: boolean | ((prev: boolean) => boolean)) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
};

const AppLayoutContext = createContext<AppLayoutValue | null>(null);

export function AppLayoutProvider({ children }: { children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [sidebarIsFixed, setSidebarIsFixed] = useState(false);

  return (
    <AppLayoutContext.Provider
      value={{ sidebarIsOpen, setSidebarIsOpen, sidebarIsFixed, setSidebarIsFixed, scrollRef }}
    >
      {children}
    </AppLayoutContext.Provider>
  );
}

export function useAppLayout(): AppLayoutValue {
  const ctx = useContext(AppLayoutContext);
  if (!ctx) {
    throw new Error("useAppLayout must be used within an AppLayoutProvider");
  }
  return ctx;
}
